// src/pages/public/Aprende.jsx
// Página /aprende — Centro de aprendizaje para clientes con acceso.
// Layout idéntico a ComoFunciona/Recomendaciones: Sidebar + Topbar + main.
// 100% tokens (var(--…)) → respeta dark/light automáticamente.
//
// Tres bloques: tutoriales en video (YouTube embed), guía oficial
// (visor PDF como imágenes paginadas — solo lectura), y conceptos clave.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import Sidebar from '../../components/Sidebar';
import Topbar from '../../components/Topbar';
import '../../index.css';
import { DashboardThemeProvider } from '../../theme/DashboardTheme';
import GuiaHandball from '../../components/GuiaHandball';

// ─── Tutoriales (editar este array para agregar más videos) ────────
const VIDEOS = [
  {
    id: '7AtY9yNmDTg',
    titulo: 'Cómo usar Edge Elite · Tutorial paso a paso (Parte 1)',
    desc: 'Empieza por acá si recién llegas.',
  },
  {
    id: '8CgICazATfs',
    titulo: 'Estrategia Ganadora Over/Under total partido · Reglas',
    desc: 'Las reglas para identificar oportunidades de valor.',
  },
  {
    id: 'LXDqs0aX2j8',
    titulo: 'Manejo de banca en Edge Elite para ser rentable',
    desc: 'Gestión de banca y stake — cómo proteger tu capital (Cap. 7 de la guía).',
  },
];

// ─── Documentos oficiales (PDFs convertidos a PNGs en build) ───────
// Cada documento tiene su carpeta y su conteo de páginas. Re-correr el
// script PyMuPDF y BUMPEAR DOCS_VERSION cada vez que se actualicen los
// PNGs (Firebase Hosting los sirve immutable + max-age 1 año; mismo
// filename = browser usa la copia vieja sin revalidar).
const DOCS_VERSION = 'v21';
const DOCS = [
  {
    id: 'guia',
    title: 'Estrategia ganadora para apostar manualmente',
    icon: '📘',
    folder: '/aprende',
    pages: 14,
  },
  {
    id: 'manual',
    title: 'Manual para ser rentable con Edge Elite',
    icon: '📗',
    folder: '/aprende/manual',
    pages: 9,
  },
];

// ─── Visor PDF (imágenes paginadas, solo lectura) ──────────────────
// Toma { folder, pages, title, icon } como props. Cada instancia
// maneja su propia página activa.
function PdfViewer({ folder, pages, title, icon }) {
  const [page, setPage] = useState(1);

  const next = () => setPage((p) => Math.min(pages, p + 1));
  const prev = () => setPage((p) => Math.max(1, p - 1));

  // Navegación con teclado ← / → / PageUp / PageDown.
  // Nota: con dos visores en la misma vista, ambos escuchan teclado y
  // avanzarían en paralelo. Aceptable porque el usuario casi siempre lee
  // uno a la vez; alternativa futura: gestionar foco para enrutar teclas
  // sólo al visor activo.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        setPage((p) => Math.min(pages, p + 1));
      }
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        setPage((p) => Math.max(1, p - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pages]);

  // Prevenir context menu (clic derecho → "guardar imagen") en el stage.
  // Protección razonable, no infalible: la URL sigue accesible para alguien
  // que inspeccione la pestaña Red del navegador (decisión documentada).
  const blockContextMenu = (e) => e.preventDefault();

  const src = `${folder}/page-${String(page).padStart(2, '0')}.png?v=${DOCS_VERSION}`;

  return (
    <div className="apr-pdf">
      <div className="apr-pdf-head">
        <div className="apr-pdf-title">{icon} {title}</div>
        <div className="apr-pdf-lock">🔒 Solo lectura</div>
      </div>

      <div
        className="apr-pdf-stage"
        onContextMenu={blockContextMenu}
        role="region"
        aria-label={`Visor: ${title}`}
      >
        <img
          src={src}
          alt={`Página ${page} de ${pages}`}
          className="apr-pdf-img"
          onContextMenu={blockContextMenu}
          draggable={false}
        />
      </div>

      <div className="apr-pdf-nav">
        <button
          type="button"
          className="apr-pdf-btn"
          onClick={prev}
          disabled={page === 1}
          aria-label="Página anterior"
        >
          ← Anterior
        </button>
        <div className="apr-pdf-counter">
          Página <b>{page}</b> de {pages}
        </div>
        <button
          type="button"
          className="apr-pdf-btn"
          onClick={next}
          disabled={page === pages}
          aria-label="Página siguiente"
        >
          Siguiente →
        </button>
      </div>
    </div>
  );
}

// ─── Aviso destacado "IMPORTANTE" sobre los documentos ─────────────
function MotorWarning() {
  return (
    <div className="apr-warning" role="note">
      <div className="apr-warning-title">⚠ IMPORTANTE</div>
      <p className="apr-warning-text">
        Estas guías y manuales funcionan únicamente con el motor Edge Elite.
        Todo lo que aprendas aquí depende del motor: sus alertas, su edge,
        sus stats en vivo y sus gráficos. Sin el motor, no sirven de nada.
      </p>
    </div>
  );
}

// ─── Card de video YouTube embebido (responsive 16:9) ──────────────
function VideoCard({ id, titulo, desc }) {
  return (
    <article className="apr-video">
      <div className="apr-video-frame">
        <iframe
          src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`}
          title={titulo}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="apr-video-meta">
        <h3 className="apr-video-title">{titulo}</h3>
        <p className="apr-video-desc">{desc}</p>
      </div>
    </article>
  );
}

// ─── Card de concepto clave ─────────────────────────────────────────
function ConceptCard({ icon, title, body }) {
  return (
    <div className="apr-concept">
      <div className="apr-concept-icon" aria-hidden="true">{icon}</div>
      <div className="apr-concept-title">{title}</div>
      <div className="apr-concept-body">{body}</div>
    </div>
  );
}

// ─── Contenido (sin layout) ─────────────────────────────────────────
function AprendeContent() {
  return (
    <div className="page">
      <div className="apr-eyebrow">CENTRO DE APRENDIZAJE</div>
      <h1 className="apr-title">Aprende a usar Edge Elite</h1>
      <p className="apr-subtitle">
        Tutoriales en video, la guía oficial de la estrategia, y los conceptos clave que necesitas dominar para sacarle todo el jugo a Edge Elite.
      </p>

      {/* ── Tutoriales — grid uniforme: todos los videos del mismo tamaño ── */}
      {/* ── Estrategia Handball (2026-09-04) — arriba para que sea visible
             sin scrollear el visor de documentos ── */}
      <GuiaHandball />

      <section className="apr-section">
        <h2 className="apr-section-h">Tutoriales</h2>
        <div className="apr-video-grid">
          {VIDEOS.map((v) => (
            <VideoCard key={v.id} {...v} />
          ))}
        </div>
      </section>

      {/* ── Documentos (aviso destacado + visores) ── */}
      <section className="apr-section">
        <h2 className="apr-section-h">Documentos oficiales</h2>
        <MotorWarning />
        {DOCS.map((d) => (
          <PdfViewer
            key={d.id}
            folder={d.folder}
            pages={d.pages}
            title={d.title}
            icon={d.icon}
          />
        ))}
      </section>

      {/* ── Conceptos clave ── */}
      <section className="apr-section">
        <h2 className="apr-section-h">Conceptos clave</h2>
        <div className="apr-concepts">
          <ConceptCard
            icon="📊"
            title="Qué es el edge"
            body="El indicador de ventaja que el motor detecta en cada partido. Cuando supera el umbral, aparece como señal en tu dashboard y en Telegram, lista para que la leas."
          />
          <ConceptCard
            icon="⏱"
            title="Las ventanas"
            body="Los momentos del partido donde la señal tiene más valor. El motor solo emite en ventanas calibradas, en minutos específicos de cada cuarto."
          />
          <ConceptCard
            icon="💰"
            title="Gestión de banca"
            body="1 unidad = 1–2% de tu bankroll. Cada señal trae el stake recomendado (1.5u o 2u) según la fuerza de la oportunidad."
          />
        </div>
      </section>
    </div>
  );
}

// ─── Wrapper con layout y theme provider ────────────────────────────
export default function Aprende() {
  const navigate = useNavigate();
  const { user: authUser, userData } = useAuth();
  const user = {
    uid: authUser?.uid || null,
    displayName: userData?.nombre || authUser?.displayName || 'Cliente',
    email: authUser?.email || null,
  };

  const [picks, setPicks] = useState([]);
  const [connected, setConnected] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Listener de /picks para alimentar el Sidebar (ligas en vivo)
  // y el Topbar (status conectado). Mismo patrón que ComoFunciona.
  useEffect(() => {
    const picksRef = ref(db, 'picks');
    const unsub = onValue(
      picksRef,
      (snap) => {
        setConnected(true);
        const data = snap.val() || {};
        const list = Object.entries(data).map(([id, v]) => ({ id, ...v }));
        setPicks(list);
      },
      () => setConnected(false)
    );
    return () => unsub();
  }, []);

  function handleNav(p, league) {
    setSidebarOpen(false);
    if (p === 'signals') navigate('/historial');
    else if (p === 'como-funciona') navigate('/como-funciona');
    else if (p === 'recomendaciones') navigate('/recomendaciones');
    else if (p === 'aprende') navigate('/aprende');
    else navigate('/dashboard');
  }

  return (
    <DashboardThemeProvider>
      <div className="app-layout app dashboard-scoped">
        <Helmet>
          <title>Aprende · Edge Elite</title>
        </Helmet>

        <Sidebar
          page="aprende"
          setPage={(p) => handleNav(p)}
          selectedLeague="all"
          setLeague={(l) => handleNav('live', l)}
          picks={picks}
          user={user}
          userData={userData}
          isAdmin={false}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <div className="main-content main">
          <Topbar
            connected={connected}
            lastUpdate={null}
            getAgeStr={() => '—'}
            getAgeColor={() => 'var(--muted)'}
            onMenuOpen={() => setSidebarOpen(true)}
            picks={picks}
          />
          <AprendeContent />
        </div>
      </div>

      {/* Estilos inline scope-local (todo con tokens var(--…) — dark/light auto). */}
      <style>{`
        /* ── Header ── */
        .apr-eyebrow {
          display: inline-block;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--accent);
          background: var(--accent-soft);
          padding: 4px 10px;
          border-radius: 999px;
          margin: 6px 0 14px;
        }
        .apr-title {
          font-family: var(--font-display, 'Inter Tight', sans-serif);
          font-size: 32px;
          font-weight: 800;
          letter-spacing: -0.025em;
          line-height: 1.05;
          color: var(--ink);
          margin: 0 0 10px;
        }
        .apr-subtitle {
          font-size: 14.5px;
          line-height: 1.55;
          color: var(--muted);
          max-width: 640px;
          margin: 0 0 32px;
        }
        .apr-section { margin-bottom: 36px; }
        .apr-section-h {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--muted-2);
          margin: 0 0 14px;
        }

        /* ── Videos ── */
        .apr-video {
          background: var(--bg-elev);
          border: 1px solid var(--line);
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 16px;
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .apr-video:hover { transform: translateY(-1px); }
        .apr-video-frame {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 9;
          background: #000; /* letterbox del player Youtube — mantiene en ambos modos */
        }
        .apr-video-frame iframe {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          border: 0;
        }
        .apr-video-meta { padding: 14px 18px 18px; }
        .apr-video-title {
          font-family: var(--font-display, 'Inter Tight', sans-serif);
          font-size: 17px; font-weight: 700;
          letter-spacing: -0.012em;
          color: var(--ink);
          margin: 0 0 6px;
        }
        .apr-video-desc {
          font-size: 13.5px;
          color: var(--muted);
          line-height: 1.5;
          margin: 0;
        }
        /* Grid uniforme: TODOS los videos del mismo tamaño.
           - desktop (cabe ≥640px): 2 columnas iguales 1fr / 1fr
           - mobile (<640px): 1 columna, ancho completo del contenedor
           - 3+ videos a futuro: el grid acomoda parejo (3 = 2 arriba + 1 abajo
             del mismo ancho; 4 = 2x2). Si se prefiere ≥3 cols en pantallas
             muy anchas, cambiar a auto-fit/minmax(320px, 1fr). */
        .apr-video-grid {
          display: grid;
          gap: 18px;
          grid-template-columns: 1fr;
          align-items: start;
        }
        @media (min-width: 640px) {
          .apr-video-grid { grid-template-columns: repeat(2, 1fr); }
        }

        /* ── Aviso destacado "IMPORTANTE" ────────────────────
           Look fijo (oscuro + acento naranja) en ambos modos —
           el cliente pidió específicamente este aspecto. */
        .apr-warning {
          max-width: 540px;
          margin: 0 auto 20px;
          background: #0A0A0C;
          border-left: 6px solid #FF6B2C;
          border-radius: 12px;
          padding: 16px 20px;
        }
        .apr-warning-title {
          font-family: var(--font-display, 'Inter Tight', sans-serif);
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.10em;
          text-transform: uppercase;
          color: #FF6B2C;
          margin: 0 0 6px;
        }
        .apr-warning-text {
          font-size: 13.5px;
          line-height: 1.55;
          color: #E5E5EA;
          margin: 0;
        }
        @media (max-width: 600px) {
          .apr-warning { padding: 14px 16px; }
          .apr-warning-text { font-size: 13px; }
        }

        /* ── Visor PDF ── */
        /* Acotado a ~540px: respeta proporción A4 vertical sin estirarse a
           todo el ancho. Centrado horizontalmente como un documento. */
        .apr-pdf {
          max-width: 540px;
          margin: 0 auto 24px;
          background: var(--bg-elev);
          border: 1px solid var(--line);
          border-radius: 16px;
          overflow: hidden;
        }
        .apr-pdf:last-child { margin-bottom: 0; }
        .apr-pdf-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 16px;
          background: var(--bg-subtle);
          border-bottom: 1px solid var(--line);
        }
        .apr-pdf-title {
          font-size: 13.5px;
          font-weight: 700;
          color: var(--ink);
          letter-spacing: -0.005em;
        }
        .apr-pdf-lock {
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 4px 10px;
          border-radius: 999px;
          background: var(--accent-soft);
          color: var(--accent);
          white-space: nowrap;
        }
        /* Stage oscuro #0A0A0C como marco externo. Padding moderado:
           las páginas del PDF traen sus propios márgenes A4 blancos
           (es el aire del documento original), así que el padding del
           stage solo aporta un marco oscuro fino sin acumular margen. */
        .apr-pdf-stage {
          background: #0A0A0C;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          min-height: 320px;
          user-select: none;
          -webkit-user-select: none;
        }
        .apr-pdf-img {
          max-width: 100%;
          height: auto;
          display: block;
          box-shadow: 0 6px 20px -6px rgba(0,0,0,.28);
          border-radius: 6px;
          /* Bloqueos contra "guardar imagen" / drag → protección casual. */
          user-select: none;
          -webkit-user-select: none;
          -webkit-user-drag: none;
          pointer-events: none;
        }
        .apr-pdf-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 12px 16px;
          border-top: 1px solid var(--line);
          background: var(--bg-elev);
        }
        .apr-pdf-btn {
          font-family: inherit;
          font-size: 13px;
          font-weight: 600;
          padding: 8px 14px;
          border-radius: 8px;
          border: 1px solid var(--line);
          background: var(--bg-elev);
          color: var(--ink);
          cursor: pointer;
          transition: background .12s, border-color .12s, color .12s;
        }
        .apr-pdf-btn:hover:not(:disabled) {
          background: var(--bg-subtle);
          border-color: var(--line-strong);
        }
        .apr-pdf-btn:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
        .apr-pdf-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .apr-pdf-counter {
          font-size: 12.5px;
          color: var(--muted);
          font-variant-numeric: tabular-nums;
        }
        .apr-pdf-counter b {
          color: var(--ink);
          font-weight: 700;
        }

        /* Print: ocultar el visor para evitar Ctrl+P → impreso */
        @media print {
          .apr-pdf-img { display: none !important; }
          .apr-pdf-stage::after {
            content: 'La guía está disponible solo en pantalla.';
            color: var(--muted);
            font-style: italic;
          }
        }

        /* ── Conceptos ── */
        .apr-concepts {
          display: grid;
          gap: 14px;
          grid-template-columns: 1fr;
        }
        @media (min-width: 700px) {
          .apr-concepts { grid-template-columns: repeat(3, 1fr); }
        }
        .apr-concept {
          background: var(--bg-elev);
          border: 1px solid var(--line);
          border-radius: 14px;
          padding: 16px 18px;
          transition: border-color .15s ease;
        }
        .apr-concept:hover { border-color: var(--line-strong); }
        .apr-concept-icon {
          font-size: 22px;
          margin-bottom: 8px;
          line-height: 1;
        }
        .apr-concept-title {
          font-family: var(--font-display, 'Inter Tight', sans-serif);
          font-size: 15px;
          font-weight: 700;
          color: var(--ink);
          margin-bottom: 4px;
        }
        .apr-concept-body {
          font-size: 13px;
          color: var(--muted);
          line-height: 1.5;
        }

        /* ── Mobile tweaks ── */
        @media (max-width: 600px) {
          .apr-title { font-size: 26px; }
          .apr-subtitle { font-size: 14px; }
          .apr-pdf-head {
            flex-wrap: wrap;
            gap: 8px;
          }
          .apr-pdf-stage { padding: 12px; min-height: 280px; }
          .apr-pdf-nav {
            flex-wrap: wrap;
            gap: 8px;
          }
          .apr-pdf-counter {
            order: -1;
            flex: 1 1 100%;
            text-align: center;
            margin-bottom: 2px;
          }
        }
      `}</style>
    </DashboardThemeProvider>
  );
}
